import localhost from "apis/localhost";
import { checkedTypes, modalTypes } from "constants/types";
import parse from "html-react-parser";
import { folderBy } from "layout/dictionarySearch/constants";
import { showErrorModal } from "slices/app/actions/error";
import { hideModal, showModal } from "slices/app/actions/modals";
import {
  addToJobQueue,
  removeFromJobQueue,
  setActionRequired,
} from "slices/app/appSlice";
import { refreshView } from "slices/dictionarySearch/actions/config";
import {
  setCategories,
  setDictionaries,
} from "slices/dictionarySearch/dictionarySearchSlice";
import { isExcel, renameKeys } from "utils";

import { selectConfigFolderBy } from "../selectors";

const finalKeys = {
  idx: "index",
  fq: "occurrences",
  cats: "categories",
  cNme: "name",
  cC: "colour",
  cDesc: "description",
  iNme: "name",
  iDesc: "description",
  docs: "documents",
  dict: "dictionary",
  nme: "name",
  wc: "wordCount",
  pc: "paragraphCount",
  iWc: "wildcard",
  iCs: "caseSensitive",
  iC: "colour",
  tt: "dictionaryTermType",
};

const setExpandedAndChecked = (data) => ({
  ...data,
  documents: data?.documents?.map((d) => ({
    ...d,
    checkState:
      d.wordCount > 0 || (isExcel(d.name) && d.paragraphCount > 0)
        ? checkedTypes.CHECKED
        : checkedTypes.EMPTY,
  })),
  dictionary: {
    ...data.dictionary,
    categories: data.dictionary.categories.map((category) => ({
      ...category,
      expanded: false,
    })),
  },
});

const collapseDocuments = (data) =>
  data.map((d) => ({ ...d, expanded: false }));

export const fetchCategories = (by, folderId, navigate) => async (dispatch) => {
  dispatch(addToJobQueue("fetchCategories"));
  const params = {
    folderId,
  };
  await localhost
    .get(`/dictionarySearch/folder/${by}/categories`, {
      params,
    })
    .then(async (response) => {
      let { data } = response;
      data = renameKeys(data, finalKeys);
      data =
        by === folderBy.folder
          ? setExpandedAndChecked(data)
          : collapseDocuments(data);
      dispatch(setCategories(data));
    })
    .catch((error) => {
      if (error.response?.status === 409) {
        dispatch(
          setActionRequired({
            message: parse(error.response.data.error),
            buttonContent: "Refresh",
            buttonAction: () =>
              dispatch(refreshView(true, by, folderId, undefined, navigate)),
          })
        );
      } else {
        dispatch(showErrorModal(error));
      }
    })
    .finally(() => {
      dispatch(removeFromJobQueue("fetchCategories"));
    });
};

export const fetchDocument =
  (folderId, docId, navigate) => async (dispatch) => {
    dispatch(addToJobQueue("fetchDocument"));
    const params = {
      folderId,
      docId,
    };
    await localhost
      .get("/dictionarySearch/document/categories", {
        params,
      })
      .then(async (response) => {
        let { data } = response;
        data = renameKeys(data, finalKeys);
        data = collapseDocuments(data);
        dispatch(setCategories(data));
      })
      .catch((error) => {
        if (error.response?.status === 409) {
          dispatch(
            setActionRequired({
              message: parse(error.response.data.error),
              buttonContent: "Refresh",
              buttonAction: () =>
                dispatch(
                  refreshView(
                    false,
                    folderBy.document,
                    folderId,
                    docId,
                    navigate
                  )
                ),
            })
          );
        } else {
          dispatch(showErrorModal(error));
        }
      })
      .finally(() => {
        dispatch(removeFromJobQueue("fetchDocument"));
      });
  };

export const getDictionaries = (docId) => async (dispatch) => {
  dispatch(addToJobQueue("getDictionaries"));
  await localhost
    .get("/dictionarySearch/change/list")
    .then(async (response) => {
      const { data } = response;
      dispatch(setDictionaries(data));
      dispatch(showModal(modalTypes.CHANGE_DICTIONARY, { docId }));
    })
    .catch((error) => {
      dispatch(showErrorModal(error));
    })
    .finally(() => {
      dispatch(removeFromJobQueue("getDictionaries"));
    });
};

const replaceDocument = (documents, targetId, newDocument) => {
  const oldDocIndex = documents.findIndex((d) => d.id === targetId);

  return documents.map((d, index) => {
    if (index === oldDocIndex) {
      return newDocument;
    }
    return d;
  });
};

export const setNewDictionary =
  ({ dictionaryId, folderId, documentId }) =>
  async (dispatch, getState) => {
    dispatch(hideModal(modalTypes.CHANGE_DICTIONARY));
    dispatch(addToJobQueue("setNewDictionary"));
    const state = getState();
    const stateFolderBy = selectConfigFolderBy(state);
    const url = stateFolderBy === folderBy.folder ? "folder" : "document";

    const params = {
      folderId,
      documentId,
      dictionaryId,
    };
    await localhost
      .post(`/dictionarySearch/change/${url}`, null, {
        params,
      })
      .then(async (response) => {
        let { data } = response;
        data = renameKeys(data, finalKeys);
        data =
          stateFolderBy === folderBy.folder
            ? setExpandedAndChecked(data)
            : collapseDocuments(
                replaceDocument(
                  getState().dictionarySearch.documents,
                  documentId,
                  data[0]
                )
              );
        dispatch(setCategories(data));
      })
      .catch((error) => {
        dispatch(showErrorModal(error));
      })
      .finally(() => {
        dispatch(removeFromJobQueue("setNewDictionary"));
      });
  };

export const setDictionaryToAllDocs =
  ({ dictionaryId, folderId }) =>
  async (dispatch, getState) => {
    dispatch(hideModal(modalTypes.CHANGE_DICTIONARY));
    dispatch(addToJobQueue("setDictionaryToAllDocs"));
    const params = {
      folderId,
      dictionaryId,
    };
    await localhost
      .post("/dictionarySearch/change/documents", null, {
        params,
      })
      .then(async (response) => {
        let { data } = response;
        data = renameKeys(data, finalKeys);
        data =
          getState().dictionarySearch.config.folderBy === folderBy.folder
            ? setExpandedAndChecked(data)
            : collapseDocuments(replaceDocument(data));
        dispatch(setCategories(data));
      })
      .catch((error) => {
        dispatch(showErrorModal(error));
      })
      .finally(() => {
        dispatch(removeFromJobQueue("setDictionaryToAllDocs"));
      });
  };
